import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import React, { useState, useEffect, useCallback } from 'react';
import addDays from 'date-fns/addDays';
import set from 'lodash/fp/set';
import setDayOfYear from 'date-fns/setDayOfYear';
import lastDayOfYear from 'date-fns/lastDayOfYear';
import { startOfDay } from 'date-fns';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import ThemeProvider from '@mui/styles/ThemeProvider';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import 'react-datepicker/dist/react-datepicker.css';
import i18n from '../../../../locales';
import { STATUSES } from '../../../../constants/Orders';
import DividerText from './DividerText';
import FiltersDrawer from '../../../FiltersDrawer';
import Select from './Select';
import ToggleButton from './ToggleButton';
import { useLiveListApi } from '../../../../apis';
import { fetchPlants as fetchPlantsLiveList } from '../../../../services/plants';
import { fetchShipToLocations as fetchShipToLocationsLiveList } from '../../../../services/shipToLocations';
import { renderData } from '../../../../utils/orders';
import { newDate } from '../../../../utils/formatters';
import theme from '../../../../theme';
import {
	getDateFormatForLocale,
	getDatePickerLocale,
	resolveSupportedLocale,
} from '../../../../locales/localeConfig';

const dateTypes = {
	orderDate: 'order_date',
	dueDate: 'due_date',
};

const dateTypeKeys = Object.keys(dateTypes);

const now = new Date();
const timeframes = [
	{
		label: 'all',
		value: 'all',
	},
	{
		label: 'this_year',
		value: 'thisYear',
	},
	{
		label: 'last_7_days',
		value: 'last7Days',
	},
];

const defaultDateFilterValue = {
	timeframe: 'all',
	type: 'timeframe',
};

export const DefaultFiltering = {
	orderDate: defaultDateFilterValue,
};

function OrderFilters({ value, onFilter, children, ...props }) {
	const { t } = useTranslation('orders', 'common');
	const [filters, setFilters] = useState(value || DefaultFiltering);
	const [isOpen, setIsOpen] = useState(false);
	const toggleOpen = () => setIsOpen(!isOpen);
	const [anchorEl, setAnchorEl] = useState(null);

	const { get: getShipToLiveList, cancel: cancelShipToLiveList } =
		useLiveListApi();
	const { get: getPlantsLiveList, cancel: cancelPlantsLiveList } =
		useLiveListApi();
	const [isLoadingShipToAddresses, setIsLoadingShipToAddresses] =
		useState(false);
	const [shipToAddressOptions, setShipToAddressOptions] = useState([]);
	const [isLoadingPlants, setIsLoadingPlants] = useState(false);
	const [plantOptions, setPlantOptions] = useState([]);

	const resolvedLocale = resolveSupportedLocale(i18n.language);
	const localDate = getDateFormatForLocale(resolvedLocale);
	const datePickerLocale = getDatePickerLocale(resolvedLocale);

	const isDueDateFilter = filters.dueDate != null;
	const dateFilterType = isDueDateFilter ? 'dueDate' : 'orderDate';
	const isDateRangeFilter = filters[dateFilterType]?.type === 'dateRange';
	const startDate =
		filters[dateFilterType]?.startDate == null
			? null
			: new Date(filters[dateFilterType]?.startDate);
	const endDate =
		filters[dateFilterType]?.endDate == null
			? null
			: new Date(filters[dateFilterType]?.endDate);

	/** Placeholder text for date picker */
	const textValue =
		isDateRangeFilter && startDate && endDate
			? `${newDate(startDate)} - ${newDate(endDate)}`
			: '';

	/**
	 * Handler for DateRange picker
	 * @param {*} dates
	 */
	const onChange = (dates) => {
		const [start, end] = dates;

		const newValue = {
			startDate: start,
			endDate: end,
			timeframe: undefined,
			type: 'dateRange',
		};

		setFilters(set(dateFilterType, newValue)(filters));
	};

	/**
	 * Handler for Timeframe select
	 * @param {*} timeframe selected timeframe such as 'all', 'last7Days', 'thisYear'
	 */
	const onTimeframeChange = (timeframe) => {
		let start;
		let end;
		switch (timeframe) {
			case 'thisYear':
				start = startOfDay(setDayOfYear(now, 1));
				end = startOfDay(lastDayOfYear(now));
				break;

			case 'last7Days':
				start = startOfDay(addDays(now, -7));
				end = startOfDay(now);
				break;

			default:
				start = undefined;
				end = undefined;
				break;
		}

		setFilters(
			set(dateFilterType, {
				startDate: start,
				endDate: end,
				timeframe,
				type: 'timeframe',
			})(filters)
		);
	};

	// ----------------------------------
	// Fetch Ship-To Locations for user
	// ----------------------------------
	const fetchShipToLocations = useCallback(async () => {
		setIsLoadingShipToAddresses(true);

		const response = await fetchShipToLocationsLiveList(getShipToLiveList);

		if (response?.isAborted) {
			setIsLoadingShipToAddresses(false);
			return;
		}

		if (response?.isError) {
			console.error(
				response?.error?.message || 'Error loading Ship-To Locations'
			);
			setShipToAddressOptions([]);
			setIsLoadingShipToAddresses(false);
			return;
		}

		setShipToAddressOptions(response?.options || []);
		setIsLoadingShipToAddresses(false);
	}, [getShipToLiveList]);

	// ----------------------------------
	// Fetch Plants for user
	// ----------------------------------
	const fetchPlants = useCallback(async () => {
		setIsLoadingPlants(true);

		const response = await fetchPlantsLiveList(getPlantsLiveList);

		if (response?.isAborted) {
			setIsLoadingPlants(false);
			return;
		}

		if (response?.isError) {
			console.error(response?.error?.message || 'Error loading Plants');
			setPlantOptions([]);
			setIsLoadingPlants(false);
			return;
		}

		setPlantOptions(response?.options || []);
		setIsLoadingPlants(false);
	}, [getPlantsLiveList]);

	useEffect(() => {
		fetchShipToLocations();
		fetchPlants();

		return () => {
			cancelShipToLiveList();
			cancelPlantsLiveList();
		};
	}, [
		cancelPlantsLiveList,
		cancelShipToLiveList,
		fetchPlants,
		fetchShipToLocations,
	]);

	return (
		<FiltersDrawer
			onApply={() => {
				onFilter(filters);
			}}
			onReset={() => {
				onFilter(DefaultFiltering);
				setFilters(DefaultFiltering);
			}}
			onOpen={() => {
				setFilters(value || DefaultFiltering);
			}}
			{...props}
		>
			{({ Section, InputContainer }) => (
				<>
					<Section isFirst header={t('date')}>
						<InputContainer>
							<ToggleButton
								buttons={Object.keys(dateTypes).map((key) => ({
									label: t(`${dateTypes[key]}`),
									value: key,
								}))}
								value={dateFilterType}
								onChange={(selected) => {
									setFilters({
										...filters,
										[selected]:
											filters[dateFilterType] ?? defaultDateFilterValue, // set the newly-selected property to the existing value
										[dateFilterType]: undefined, // set the currently-selected property to undefined (swapping values)
									});
								}}
								data-testid="filters_date_type"
								data-testvalue={dateFilterType}
							/>
						</InputContainer>

						<InputContainer style={{ margin: theme.spacing(2, 0) }}>
							<FormControl variant="outlined" fullWidth>
								<InputLabel shrink>{t('timeframe')}</InputLabel>
								<Select
									displayEmpty
									size="small"
									value={
										isDateRangeFilter
											? ''
											: filters[dateFilterType]?.timeframe ?? 'all'
									}
									onChange={({ target: { value: _value } }) => {
										onTimeframeChange(_value);
									}}
									input={<OutlinedInput notched label={t('timeframe')} />}
								>
									{timeframes.map(({ label, value: _value }) => (
										<MenuItem value={_value} key={label}>
											{t(`${label}`)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</InputContainer>

						<DividerText>{t('or')}</DividerText>

						<InputContainer style={{ margin: theme.spacing(2, 0) }}>
							<div
								onClick={toggleOpen}
								onKeyPress={toggleOpen}
								ref={(el) => el && setAnchorEl(el)}
								role="button"
								tabIndex={0}
							>
								<FormControl variant="outlined" fullWidth>
									<InputLabel margin="dense" size="small">
										{t('date_range')}
									</InputLabel>
									<OutlinedInput
										size="small"
										color="primary"
										margin="dense"
										endAdornment={
											<InputAdornment position="end">
												<IconButton
													aria-hidden="false"
													role="button"
													aria-label="calendar"
												>
													<CalendarTodayIcon
														aria-label="calendar"
														role="graphics-symbol"
														aria-hidden="true"
													/>
												</IconButton>
											</InputAdornment>
										}
										label={t('date_range')}
										value={textValue}
										placeholder={t('date_range')}
										fullWidth
									/>
								</FormControl>
							</div>
							<Popover
								open={isOpen}
								onClose={() => setIsOpen(false)}
								anchorEl={anchorEl}
							>
								<ThemeProvider theme={theme}>
									<DatePicker
										size="small"
										selectsRange
										startDate={isDateRangeFilter ? startDate : null}
										endDate={isDateRangeFilter ? endDate : null}
										onChange={onChange}
										inline
										locale={datePickerLocale}
										dateFormat={localDate}
										monthsShown={2}
									/>
								</ThemeProvider>
							</Popover>
						</InputContainer>
					</Section>

					<Section header={t('current_status')} style={{ marginTop: '0.5rem' }}>
						{Object.keys(STATUSES).map((key) => (
							<div key={key}>
								<FormControlLabel
									label={t(`statuses.${key}`)}
									control={
										<Checkbox
											checked={filters.status?.[key] ?? false}
											onClick={({ target: { checked } }) =>
												setFilters(set(`status.${key}`, checked)(filters))
											}
										/>
									}
								/>
							</div>
						))}
					</Section>

					<Section header={t('locations')}>
						<InputContainer style={{ margin: theme.spacing(2, 0) }}>
							<FormControl variant="outlined" fullWidth>
								<InputLabel shrink>{t('ship_to')}</InputLabel>
								<Select
									displayEmpty
									size="small"
									disabled={isLoadingShipToAddresses}
									value={filters.shipToLocation ?? ''}
									onChange={({ target: { value: _value } }) =>
										setFilters(set('shipToLocation', _value)(filters))
									}
									input={<OutlinedInput notched label={t('ship_to')} />}
								>
									<MenuItem value="" size="small" key={999999999}>
										{t('all')}
									</MenuItem>

									{shipToAddressOptions.map((row) => (
										<MenuItem key={row.value} value={row.value} size="small">
											{renderData((row.label || '').trim())}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</InputContainer>

						<InputContainer style={{ margin: theme.spacing(2, 0) }}>
							<FormControl variant="outlined" fullWidth>
								<InputLabel
									style={{ backgroundColor: '#ffffff', paddingRight: '.25rem' }}
									shrink
								>
									{t('site')}
								</InputLabel>
								<Select
									displayEmpty
									size="small"
									disabled={isLoadingPlants}
									value={filters.siteLocation ?? ''}
									onChange={({ target: { value: _value } }) =>
										setFilters(set('siteLocation', _value)(filters))
									}
									input={<OutlinedInput notched label={t('site')} />}
								>
									<MenuItem value="" key={999999999}>
										{t('all')}
									</MenuItem>
									{plantOptions.map((row) => (
										<MenuItem key={row.value} value={row.value}>
											{renderData(row.label || '')}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</InputContainer>
					</Section>
				</>
			)}
		</FiltersDrawer>
	);
}

OrderFilters.propTypes = {
	value: PropTypes.object.isRequired,
	onFilter: PropTypes.func.isRequired,
	children: PropTypes.node,
};

export default OrderFilters;
